import { useEffect, useMemo, useState, useSyncExternalStore } from "react";
import { useSession } from "../app/session";
import { useEncryptionMode, useSpaceKey } from "../app/encryption";
import { ApiError } from "../lib/api";
import { syncEngine } from "../lib/sync";
import { sealSpaceMedia } from "../lib/media";
import {
  enableEncryption,
  reissueRecoveryCode,
  keyMaterialFor,
  plaintextCount,
  reencryptSpace,
  type ReencryptProgress,
} from "../lib/encryption";
import { Button, Card, CardHeader } from "./ui";
import { QrCode } from "./QrCode";
import { CopyIcon, KeyIcon, LockIcon, QrIcon, WarningIcon } from "./icons";
import { cn } from "../lib/cn";
import { useT } from "../i18n/index.js";

/**
 * Шифрование этого пространства: включить, догнать написанное раньше, перенести
 * ключ на свой второй телефон.
 *
 * Последнее — именно про своё второе устройство, а не про нового человека.
 * Звать нового умеет блок приглашения: там и ссылка, которую пересылают, и свой
 * квадрат, который при случае несёт ключ вместе с приглашением. Здесь человек
 * уже участник, и приглашение ему не нужно.
 *
 * Включение идёт двумя шагами, и это не церемония. Первый шаг называет цену
 * словами, от которых нельзя отмахнуться: сервер перестанет понимать написанное,
 * обратно не выключить, а без кода и без ключа содержимое не вернёт никто.
 * Второй показывает код восстановления — единственный раз за всю жизнь
 * пространства, потому что больше он нигде не хранится. Закрывает этот шаг
 * только кнопка «Я сохранил»: у панели нет ни крестика, ни клика по фону, и это
 * намеренно — случайно закрытый экран здесь стоит человеку всей переписки.
 *
 * Перешифровка написанного раньше начинается сразу после и живёт своей жизнью:
 * проход возобновляемый (см. `reencryptSpace`), поэтому оборванный проход — это
 * «осталось N записей» и кнопка «Продолжить», а не ошибка.
 */

/** Какой из двух шагов включения сейчас на экране. */
type Stage = "resting" | "warning" | "code" | "reissueWarning";

export function EncryptionCard() {
  const { space, refresh } = useSession();
  const t = useT();

  // Движок сообщает о каждой перемене, а остаток открытых записей считается по
  // его состоянию: без подписки счётчик замер бы на первом значении.
  const snapshot = useSyncExternalStore(syncEngine.subscribe, syncEngine.getSnapshot);

  const spaceId = space?.id ?? null;
  const isOwner = space?.role === "owner";

  const mode = useEncryptionMode(spaceId);
  const { presence, recheck } = useSpaceKey(spaceId);

  const [stage, setStage] = useState<Stage>("resting");
  const [recoveryCode, setRecoveryCode] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [failure, setFailure] = useState<string | null>(null);
  const [copied, setCopied] = useState(false);

  const [progress, setProgress] = useState<ReencryptProgress | null>(null);
  const [catchUpFailed, setCatchUpFailed] = useState(false);

  const [showingKey, setShowingKey] = useState(false);
  const [material, setMaterial] = useState<string | null>(null);
  const [keyNotice, setKeyNotice] = useState<string | null>(null);

  const remaining = useMemo(
    () => (spaceId && mode === "e2ee" ? plaintextCount(spaceId) : 0),
    // `snapshot` — это и есть повод пересчитать: пачка перешифровалась, дельта
    // пришла, остаток стал меньше.
    [snapshot, spaceId, mode],
  );

  useEffect(() => {
    if (!copied) return;
    const timer = setTimeout(() => setCopied(false), 2000);
    return () => clearTimeout(timer);
  }, [copied]);

  // Ключ не остаётся на экране дольше, чем на него смотрят: смена пространства
  // убирает и квадрат, и материал из памяти компонента.
  useEffect(() => {
    setShowingKey(false);
    setMaterial(null);
    setKeyNotice(null);
  }, [spaceId]);

  if (!space || !spaceId) return null;

  async function turnOn() {
    if (!spaceId || busy) return;

    setBusy(true);
    setFailure(null);

    let enabled;
    try {
      enabled = await enableEncryption(spaceId);
    } catch (cause) {
      setStage("resting");
      setFailure(
        cause instanceof ApiError ? cause.message : t("encryption.enableFailed"),
      );
      return;
    } finally {
      setBusy(false);
    }

    /*
     * Код на экране раньше всего остального, и ничто после этой строки не имеет
     * права его убрать.
     *
     * Он существует ровно один раз за жизнь пространства и нигде не хранится.
     * Уронить экран с кодом из-за сорвавшейся сети — значит зашифровать чужую
     * переписку ключом, который никто не видел.
     */
    setRecoveryCode(enabled.recoveryCode);
    setStage("code");

    /*
     * И сессия, и состояние синхронизации всё ещё считают пространство
     * открытым. Второе важнее: `syncEngine.codec` спрашивает режим у своего
     * состояния, и пока там «none», он выдаёт — и кладёт в кэш — обычный кодек
     * вместо только что созданного шифрующего, и перешифровка отказалась бы
     * начинаться. Обе неудачи здесь молчаливые: следующая дельта сделает то же
     * самое, а человек в этот момент переписывает код.
     */
    await refresh().catch(() => undefined);
    await syncEngine.pull(spaceId);
    // Ключ только что лёг на устройство — иначе карточка ещё считает, что его
    // здесь нет, и прячет всё, что с ним связано.
    recheck();
  }

  /** «Я сохранил» — единственный выход из шага с кодом. */
  function acknowledge() {
    setRecoveryCode(null);
    setStage("resting");
    void catchUp();
  }

  /**
   * Выпустить новый код взамен потерянного.
   *
   * Ключ при этом не меняется и ничто не перешифровывается — ради этого код и
   * ключ и сделаны разными вещами. Переписывается одна обёртка.
   *
   * Старый код умирает в тот же миг, и это единственная беда, которую эта
   * кнопка способна причинить: человек выпускает новый, а в менеджере паролей
   * остаётся прежний — рабочая резервная копия молча заменена мёртвой. Поэтому
   * шаг с предупреждением здесь такой же обязательный, как при включении.
   */
  async function reissue() {
    if (!spaceId || busy) return;

    setBusy(true);
    setFailure(null);

    try {
      const next = await reissueRecoveryCode(spaceId);
      setRecoveryCode(next);
      setStage("code");
    } catch (cause) {
      setFailure(cause instanceof ApiError ? cause.message : t("encryption.reissueFailed"));
      setStage("resting");
    } finally {
      setBusy(false);
    }
  }

  async function catchUp() {
    if (!spaceId || progress) return;

    setCatchUpFailed(false);
    setProgress({ total: plaintextCount(spaceId), done: 0 });
    try {
      await reencryptSpace(spaceId, setProgress);

      /*
       * И файлы следом, тем же нажатием.
       *
       * Пока этот вызов отсутствовал, включение шифрования переписывало
       * записи и оставляло фотографии в них лежать на диске такими, как сняты,
       * — то есть ровно ту половину, ради которой конверт и написан. Отдельной
       * кнопкой их делать нельзя: человек, нажавший «продолжить», считает, что
       * догоняется всё, и второй, не нажатой кнопки просто не заметит.
       *
       * Прогресс общий: сколько чего осталось — это подробность про устройство,
       * а не про решение, которое человек принимает.
       */
      await sealSpaceMedia(spaceId, (media) =>
        setProgress({ total: media.total, done: media.done }),
      );
    } catch {
      // Сорвавшаяся сеть, закрытая крышка, ушедшая в сон вкладка. Ничего не
      // потеряно: непереписанные записи остались непереписанными, и следующий
      // проход начнётся ровно с них.
      setCatchUpFailed(true);
    } finally {
      setProgress(null);
    }
  }

  async function showKey() {
    if (!spaceId) return;

    setShowingKey(true);
    setKeyNotice(null);
    const found = await keyMaterialFor(spaceId);
    if (!found) {
      setKeyNotice(t("encryption.qrNoKey"));
      return;
    }
    setMaterial(found);
  }

  function hideKey() {
    setShowingKey(false);
    setMaterial(null);
    setKeyNotice(null);
  }

  async function copyCode() {
    if (!recoveryCode) return;
    try {
      await navigator.clipboard.writeText(recoveryCode);
      setCopied(true);
    } catch {
      // Буфер обмена закрыт вне жеста и на незащищённом источнике, а сам объект
      // на http отсутствует вовсе.
      window.prompt(t("encryption.copyManually"), recoveryCode);
    }
  }

  const meta = mode === "e2ee" ? t("encryption.stateOn") : t("encryption.stateOff");

  return (
    <Card>
      <CardHeader title={t("encryption.title")} meta={meta} />

      {stage === "warning" ? (
        <div className="flex flex-col gap-3 px-4 py-4">
          <div className="flex items-start gap-3">
            <span
              aria-hidden="true"
              className="grid size-10 shrink-0 place-items-center rounded-full bg-accent-soft text-accent-ink"
            >
              <WarningIcon className="size-5" strokeWidth={1.8} />
            </span>
            <h3 className="min-w-0 flex-1 font-display text-[0.9375rem]">
              {t("encryption.warnTitle")}
            </h3>
          </div>

          <p className="text-[0.875rem] text-ink-soft">{t("encryption.warnServer")}</p>
          <p className="text-[0.875rem] text-ink-soft">{t("encryption.warnForever")}</p>
          {/* Самая дорогая строка на экране, поэтому она — единственная, что
              лежит на собственной плашке. */}
          <p className="rounded-row bg-surface-alt px-4 py-3 text-[0.875rem]">
            {t("encryption.warnLoss")}
          </p>

          {failure ? <p className="text-[0.875rem] text-neg">{failure}</p> : null}

          <div className="flex flex-wrap items-center gap-2">
            <Button onClick={turnOn} disabled={busy}>
              {busy ? t("encryption.enabling") : t("encryption.warnConfirm")}
            </Button>
            <Button
              variant="ghost"
              disabled={busy}
              onClick={() => {
                setStage("resting");
                setFailure(null);
              }}
            >
              {t("common.cancel")}
            </Button>
          </div>
        </div>
      ) : stage === "reissueWarning" ? (
        <div className="flex flex-col gap-3 px-4 py-4">
          <h3 className="font-display text-[0.9375rem]">{t("encryption.reissueTitle")}</h3>
          <p className="text-[0.875rem] text-ink-soft">{t("encryption.reissueOldDies")}</p>
          <p className="text-[0.875rem] text-muted">{t("encryption.reissueSafe")}</p>

          <div className="flex flex-col gap-2 pt-1">
            <Button onClick={() => void reissue()} disabled={busy}>
              {busy ? t("encryption.enabling") : t("encryption.reissueConfirm")}
            </Button>
            <Button variant="ghost" onClick={() => setStage("resting")} disabled={busy}>
              {t("common.cancel")}
            </Button>
          </div>
        </div>
      ) : stage === "code" && recoveryCode ? (
        <div className="flex flex-col gap-3 px-4 py-4">
          <div className="flex items-start gap-3">
            <span
              aria-hidden="true"
              className="grid size-10 shrink-0 place-items-center rounded-full bg-accent-soft text-accent-ink"
            >
              <KeyIcon className="size-5" strokeWidth={1.8} />
            </span>
            <h3 className="min-w-0 flex-1 font-display text-[0.9375rem]">
              {t("encryption.codeTitle")}
            </h3>
          </div>

          {/* `select-all`: одно касание выделяет код целиком, и человеку не
              приходится ловить границы группы пальцем. */}
          <code
            aria-label={t("encryption.codeAria")}
            className={cn(
              "block rounded-row bg-surface-alt px-4 py-4 text-center",
              "font-mono text-[1.0625rem] leading-relaxed tracking-[0.12em] break-all select-all",
            )}
          >
            {recoveryCode}
          </code>

          <p className="text-[0.875rem] text-ink-soft">{t("encryption.codeOnce")}</p>
          <p className="text-[0.875rem] text-muted">{t("encryption.codeWhere")}</p>

          <div className="flex flex-wrap items-center gap-2">
            <Button variant="secondary" onClick={copyCode}>
              <CopyIcon className="size-5" />
              {t("encryption.copy")}
            </Button>
            <Button onClick={acknowledge}>{t("encryption.codeSaved")}</Button>
            <span role="status" className="text-[0.8125rem] text-muted">
              {copied ? t("encryption.copied") : ""}
            </span>
          </div>
        </div>
      ) : (
        <div className="flex flex-col gap-3 px-4 py-4">
          <div className="flex items-start gap-3">
            <span
              aria-hidden="true"
              className={cn(
                "grid size-10 shrink-0 place-items-center rounded-full",
                mode === "e2ee"
                  ? "bg-accent-soft text-accent-ink"
                  : "bg-surface-alt text-muted",
              )}
            >
              <LockIcon className="size-5" strokeWidth={1.8} />
            </span>
            <p className="min-w-0 flex-1 text-[0.875rem] text-ink-soft">
              {mode === "e2ee" ? t("encryption.onNote") : t("encryption.offNote")}
            </p>
          </div>

          {failure ? <p className="text-[0.875rem] text-neg">{failure}</p> : null}

          {mode === "none" ? (
            isOwner ? (
              <Button
                variant="secondary"
                className="self-start"
                onClick={() => {
                  setFailure(null);
                  setStage("warning");
                }}
              >
                {t("encryption.enable")}
              </Button>
            ) : (
              <p className="text-[0.8125rem] text-muted">{t("encryption.ownerOnly")}</p>
            )
          ) : presence !== "present" ? (
            /*
             * Пространство зашифровано, а ключа здесь нет. Ни перешифровывать,
             * ни показывать ключ такому устройству нечем — предлагать кнопки,
             * которые ему заведомо не по силам, значит врать. Открывают
             * пространство на экране замка, а не отсюда.
             */
            presence === "absent" ? (
              <p className="text-[0.8125rem] text-muted">{t("encryption.qrNoKey")}</p>
            ) : null
          ) : (
            <>
              <CatchUp
                progress={progress}
                remaining={remaining}
                failed={catchUpFailed}
                onResume={() => void catchUp()}
              />

              <div className="flex flex-col gap-3">
                <Button
                  variant={showingKey ? "ghost" : "secondary"}
                  className={cn("self-start", showingKey && "px-2 text-[0.875rem]")}
                  onClick={() => {
                    if (showingKey) hideKey();
                    else void showKey();
                  }}
                >
                  {showingKey ? null : <QrIcon className="size-5" strokeWidth={1.8} />}
                  {showingKey ? t("encryption.hideKey") : t("encryption.showKey")}
                </Button>

                {/* Рядом с показом ключа, а не в другом углу: обе кнопки
                    отвечают на один и тот же вопрос — «как не потерять
                    доступ», — и человек ищет их в одном месте. */}
                <Button
                  variant="ghost"
                  className="self-start px-2 text-[0.875rem]"
                  onClick={() => setStage("reissueWarning")}
                >
                  {t("encryption.reissue")}
                </Button>

                {showingKey ? (
                  <div className="flex flex-col items-center gap-3">
                    {material ? (
                      <>
                        <p className="self-stretch text-[0.875rem] text-ink-soft">
                          {t("encryption.qrTitle")}
                        </p>
                        <QrCode
                          value={joinUrl(spaceId, material)}
                          label={t("encryption.qrTitle")}
                        />
                        <p className="self-stretch rounded-row bg-surface-alt px-4 py-3 text-[0.8125rem] text-ink-soft">
                          {t("encryption.qrWarning")}
                        </p>
                        <p className="self-stretch text-[0.8125rem] text-muted">
                          {t("encryption.qrOwnOnly")}
                        </p>
                      </>
                    ) : keyNotice ? null : (
                      <span
                        aria-hidden="true"
                        className="size-6 animate-pulse rounded-full bg-line"
                      />
                    )}

                    {keyNotice ? (
                      <p className="self-stretch text-[0.8125rem] text-muted">
                        {keyNotice}
                      </p>
                    ) : null}
                  </div>
                ) : null}
              </div>
            </>
          )}
        </div>
      )}
    </Card>
  );
}

/**
 * Ссылка, по которой своё второе устройство открывает пространство.
 *
 * Ключ — **во фрагменте**, после решётки, и только там. Браузеры фрагмент на
 * сервер не отправляют: он не попадает ни в лог веб-сервера, ни в Referer, ни в
 * историю на той стороне (страница стирает его первым же делом). Ключ в пути
 * или в query означал бы, что весь смысл сквозного шифрования пропал на
 * последнем шаге — сервер увидел бы ключ от всего, что хранит.
 *
 * Приглашения здесь нет и не нужно: на втором телефоне тот же человек, он уже
 * участник. Квадрат с приглашением внутри собирает блок приглашения.
 */
function joinUrl(spaceId: string, material: string): string {
  const query = new URLSearchParams({ space: spaceId });

  return `${location.origin}/join?${query.toString()}#k=${material}`;
}

/**
 * Написанное до включения — сколько его осталось в открытом виде.
 *
 * Оборвавшийся проход показан как остаток и кнопка, а не как ошибка: он
 * возобновляемый по устройству, и «Продолжить» просто зовёт его снова.
 */
function CatchUp({
  progress,
  remaining,
  failed,
  onResume,
}: {
  progress: ReencryptProgress | null;
  remaining: number;
  failed: boolean;
  onResume: () => void;
}) {
  const t = useT();

  if (progress) {
    const share = progress.total > 0 ? progress.done / progress.total : 1;

    return (
      <div className="flex flex-col gap-2">
        <p role="status" className="text-[0.875rem] text-ink-soft">
          {t("encryption.catchUpRunning", {
            done: progress.done,
            total: progress.total,
          })}
        </p>
        <div className="h-1.5 w-full overflow-hidden rounded-pill bg-surface-alt">
          <div
            className="h-full rounded-pill bg-accent transition-[width] duration-300"
            style={{ width: `${Math.round(share * 100)}%` }}
          />
        </div>
      </div>
    );
  }

  if (remaining === 0) {
    return <p className="text-[0.8125rem] text-muted">{t("encryption.catchUpDone")}</p>;
  }

  return (
    <div className="flex flex-col gap-2">
      <p className="text-[0.875rem] text-ink-soft">
        {t("encryption.catchUpLeft", { count: remaining })}
      </p>
      <p className="text-[0.8125rem] text-muted">
        {failed ? t("encryption.catchUpFailed") : t("encryption.catchUpResumable")}
      </p>
      <Button variant="secondary" className="self-start" onClick={onResume}>
        {t("encryption.catchUpResume")}
      </Button>
    </div>
  );
}
